import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, Package, Flag, Timer, Camera, CameraOff, Keyboard, Undo2, Play, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { apiError, SPORT_LABEL } from "@/lib/format";
import { StatusTimeline } from "@/components/AthleteStatus";

const MODES = [
  { id: "KIT", label: "Entrega de Kit", icon: Package, cls: "bg-violet-500 text-white" },
  { id: "CHECKIN", label: "Check-in Largada", icon: Flag, cls: "bg-cyan-400 text-ink" },
  { id: "FINISH", label: "Chegada", icon: Timer, cls: "bg-brand text-white" },
];
const LEVEL = {
  success: ["border-emerald-500/50 bg-emerald-500/10", CheckCircle2, "text-emerald-300"],
  warning: ["border-amber-500/50 bg-amber-500/10", AlertTriangle, "text-amber-300"],
  error: ["border-red-500/50 bg-red-500/10", XCircle, "text-red-300"],
  info: ["border-white/20 bg-white/5", CheckCircle2, "text-slate-300"],
};

export default function Scanner() {
  const [mode, setMode] = useState("KIT");
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const lockRef = useRef(false);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const loadInfo = useCallback(() => api.get("/staff/race-starts").then((r) => setInfo(r.data)).catch(() => {}), []);
  const stopCam = useCallback(async () => {
    try { if (scannerRef.current?.isScanning) await scannerRef.current.stop(); } catch (err) { console.error("Scanner stop error:", err); }
    scannerRef.current = null;
    setCamOn(false);
  }, []);
  useEffect(() => { loadInfo(); return () => { stopCam(); }; }, [loadInfo, stopCam]);

  const process = async (code, undo = false) => {
    if (!code || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/scan", { code, mode: modeRef.current, undo });
      setResult(data);
      toast.success(data.message);
      loadInfo();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") { setResult({ ...d, ok: false, code }); toast[d.level === "warning" ? "warning" : "error"](d.message); }
      else { setResult({ ok: false, level: "error", message: apiError(e), code }); toast.error(apiError(e)); }
    } finally { setBusy(false); }
  };

  const onScan = (text) => {
    if (lockRef.current) return;
    lockRef.current = true;
    if (navigator.vibrate) navigator.vibrate(80);
    process(text).finally(() => setTimeout(() => { lockRef.current = false; }, 2500));
  };

  const startCam = async () => {
    try {
      const s = new Html5Qrcode("qr-reader");
      scannerRef.current = s;
      await s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } }, onScan, () => {});
      setCamOn(true);
    } catch (e) { toast.error("Não foi possível acessar a câmera. Use a digitação manual."); }
  };

  const raceStart = async (sport, clear = false) => {
    if (!clear && info?.race_starts?.[sport] && !window.confirm("Já existe largada registrada. Substituir pelo horário atual?")) return;
    try { const { data } = await api.post("/staff/race-start", { sport_type: sport, clear }); setInfo((i) => ({ ...i, race_starts: data.race_starts })); toast.success(clear ? "Largada removida" : "Largada registrada!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const current = MODES.find((m) => m.id === mode);

  return (
    <div className="space-y-6" data-testid="scanner-page">
      <div>
        <p className="text-brand font-bold uppercase tracking-widest text-xs">Operação do Evento</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase flex items-center gap-2"><ScanLine className="w-8 h-8 text-brand" /> Leitor QR Code</h1>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="scan-mode-tabs">
        {MODES.map((m) => (
          <button key={m.id} data-testid={`scan-mode-${m.id}`} onClick={() => { setMode(m.id); setResult(null); }}
            className={`rounded-xl p-3 sm:p-4 border font-black uppercase text-[11px] sm:text-sm flex flex-col items-center gap-1 transition-all ${mode === m.id ? `${m.cls} border-transparent scale-[1.02] shadow-lg` : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
            <m.icon className="w-5 h-5" /> {m.label}
            {info && <span className="text-[10px] font-semibold opacity-80">{info.counts[m.id]} / {info.counts.PAID}</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-surface border border-white/10 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold uppercase flex items-center gap-2"><Camera className="w-4 h-4 text-brand" /> Câmera</h3>
            {camOn ? <Button data-testid="cam-stop-btn" size="sm" variant="outline" onClick={stopCam} className="border-white/15 bg-transparent"><CameraOff className="w-4 h-4 mr-1" /> Parar</Button>
              : <Button data-testid="cam-start-btn" size="sm" onClick={startCam} className={`${current.cls} font-bold`}><Camera className="w-4 h-4 mr-1" /> Ligar câmera</Button>}
          </div>
          <div id="qr-reader" className={`rounded-xl overflow-hidden bg-black ${camOn ? "min-h-[280px]" : "h-0"}`} />
          {!camOn && <p className="text-xs text-slate-500">Ligue a câmera e aponte para o QR Code do atleta. Modo atual: <b className="text-white">{current.label}</b>.</p>}
          <form onSubmit={(e) => { e.preventDefault(); process(manual.trim()); setManual(""); }} className="flex gap-2">
            <Input data-testid="scan-manual-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ou digite o nº de peito (ex.: M1-105)" className="bg-ink border-white/10 font-mono uppercase" />
            <Button data-testid="scan-manual-btn" type="submit" disabled={busy} className="bg-white/10 hover:bg-white/20 font-bold"><Keyboard className="w-4 h-4" /></Button>
          </form>
        </div>

        <div className="space-y-4">
          {result ? (() => { const [cls, Icon, tc] = LEVEL[result.level] || LEVEL.info; return (
            <div data-testid="scan-result" data-level={result.level} className={`rounded-2xl border p-5 space-y-4 animate-fade-up ${cls}`}>
              <div className="flex items-start gap-3">
                <Icon className={`w-7 h-7 shrink-0 ${tc}`} />
                <div className="min-w-0">
                  <p className={`font-bold ${tc}`} data-testid="scan-message">{result.message}</p>
                  {result.athlete && <>
                    <p className="font-display text-3xl font-black mt-1 truncate" data-testid="scan-athlete-name">{result.athlete.name}</p>
                    <p className="text-sm text-slate-300"><span className="font-mono font-bold text-neon text-lg">{result.athlete.bib_code}</span> • {result.athlete.category} • Camisa <b>{result.athlete.shirt_size}</b></p>
                  </>}
                  {result.time && <p className="text-sm mt-1">Tempo: <b className="font-mono text-white text-lg">{result.time}</b>{result.position && <> • Colocação parcial: <b className="text-white">{result.position.position}º</b></>}</p>}
                </div>
              </div>
              {result.timeline && <StatusTimeline steps={result.timeline} result={result.position} />}
              {result.athlete && result.athlete.status === "PAID" && (result.ok || result.already) && (
                <Button data-testid="scan-undo-btn" size="sm" variant="outline" onClick={() => process(result.athlete.bib_code, true)} className="border-white/15 bg-transparent text-slate-300"><Undo2 className="w-4 h-4 mr-1" /> Desfazer {current.label}</Button>
              )}
            </div>); })() : (
            <div className="rounded-2xl bg-surface border border-dashed border-white/15 p-8 text-center text-slate-500 text-sm" data-testid="scan-empty">Aguardando leitura…</div>
          )}

          <div className="rounded-2xl bg-surface border border-white/10 p-5" data-testid="race-start-panel">
            <h3 className="font-display text-lg font-bold uppercase flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-brand" /> Largada por modalidade</h3>
            <p className="text-xs text-slate-500 mb-3">O tempo de chegada é calculado a partir deste horário.</p>
            <div className="space-y-2">
              {["CORRIDA", "CICLISMO", "KIDS"].map((s) => { const at = info?.race_starts?.[s]; return (
                <div key={s} className="flex items-center justify-between gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2" data-testid={`race-start-${s}`}>
                  <div><div className="font-bold text-sm">{SPORT_LABEL[s]}</div><div className={`text-xs ${at ? "text-emerald-300" : "text-slate-500"}`} data-testid={`race-start-time-${s}`}>{at ? `Largada: ${new Date(at).toLocaleTimeString("pt-BR")}` : "Não iniciada"}</div></div>
                  <div className="flex gap-1">
                    <Button data-testid={`race-start-btn-${s}`} size="sm" onClick={() => raceStart(s)} className="bg-brand hover:bg-brand-hover font-bold text-xs"><Play className="w-3 h-3 mr-1" /> {at ? "Reiniciar" : "Dar largada"}</Button>
                    {at && <Button data-testid={`race-clear-btn-${s}`} size="sm" variant="ghost" onClick={() => raceStart(s, true)} className="text-slate-400 text-xs">Limpar</Button>}
                  </div>
                </div>); })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
